import { getColumns } from './AggregatedRecordBuilder.Grid';
import aggregatedRecordFieldHelper from '../../../infrastructure/helpers/functions/aggregation/aggregatedRecordFieldHelper';
import AssignmentBuilder from '../../Functions/Assignment/AssignmentBuilder';
import DataSourceField from '../../../types/report/DataSourceField';
import dataSourceHelper from '../../../infrastructure/helpers/functions/common/dataSourceHelper';
import DeclarationBuilderProps from '../../Functions/Declaration/DeclarationBuilderProps';
import ErrorList from '../../Common/ErrorList/ErrorList';
import FieldDefinition from '../../../types/common/FieldDefinition';
import guidance from '../ReportConfigEditor/ReportConfigEditor.Guidance';
import InfoIcon from '../../Common/InfoIcon/InfoIcon';
import locationType from '../../../types/functions/Location/LocationType';
import React from 'react';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import typeHelper from '../../../infrastructure/helpers/common/typeHelper';
import CopyAggregationModalState from './CopyAggregationModal/CopyAggregationFieldModalState';
import store from '../../../store/store';
import actions from '../../../store/actions';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import SelectOption from '../../../infrastructure/types/SelectOption';
import reportLogicReferenceHelper from '../../../infrastructure/helpers/functions/report/reportLogicReferenceHelper';
import { onReferenceSelected } from '../../../infrastructure/helpers/report/reference/helper';
import ReportVariable from '../../../types/report/ReportVariable';
import SelectSearch, { SelectedOptionValue, SelectSearchOption } from 'react-select-search';
import aggregationHelper from '../../../infrastructure/helpers/functions/aggregation/aggregationHelper';

class AggregatedRecordBuilder extends React.Component<DeclarationBuilderProps<DataSourceField>, { selectedField: string }> {

    private defaultField: string = '';

    constructor(props: DeclarationBuilderProps<DataSourceField>) {
        super(props);

        this.onResetAssignmentClick = this.onResetAssignmentClick.bind(this);
        this.getFields = this.getFields.bind(this);
        this.getDefinitions = this.getDefinitions.bind(this);
        this.getVariables = this.getVariables.bind(this);
        this.getAssignmentContent = this.getAssignmentContent.bind(this);
        this.onCopyFieldClick = this.onCopyFieldClick.bind(this);
        this.getFieldReferences = this.getFieldReferences.bind(this);
        this.onAddFieldClick = this.onAddFieldClick.bind(this);
        this.onRemoveFieldClick = this.onRemoveFieldClick.bind(this);
        this.getOptions = this.getOptions.bind(this);
        this.onFieldSelect = this.onFieldSelect.bind(this);

        this.state = {
            selectedField: '',
        };
    }

    onCopyFieldClick(field: DataSourceField) {

        let state: CopyAggregationModalState = {
            title: 'Copy Field',
            isOpen: true,
            error: null,
            copyAllFields: false,
            sourceNumber: (this.props.declarationBatchNumber as number).toString(),
            targetNumbers: new Set(),
            sourceFieldId: field.fieldId ? `f_${field.fieldId}` : `v_${field.variableNumber}`,
            targetFieldId: field.fieldId ? `f_${field.fieldId}` : `v_${field.variableNumber}`
        };

        // on opening modal
        store.dispatch({ type: actions.copyAggregationFieldModal.change, payload: state });
    }

    onResetAssignmentClick(location: ReportLogicLocation): void {

        this.props.onResetAssignmentClick(location, true);
    }

    getFields(): DataSourceField[] {

        let location = { statementNumber: this.props.declarationBatchNumber as number } as ReportLogicLocation;

        let fields = aggregatedRecordFieldHelper.getDeclarations(this.props.reportConfig, location);

        return fields;
    }

    getDefinitions(): FieldDefinition[] {

        let location = { statementNumber: this.props.declarationBatchNumber as number } as ReportLogicLocation;

        let dataSourceId = aggregatedRecordFieldHelper.getDataSourceId(this.props.reportConfig, location);

        let dataSource = dataSourceHelper.getById(this.props.collections.dataSources, dataSourceId);

        return dataSource?.fieldDefinitions ?? [];
    }

    getVariables(): ReportVariable[] {

        let location = { statementNumber: this.props.declarationBatchNumber as number } as ReportLogicLocation;

        let dataSourceId = aggregatedRecordFieldHelper.getDataSourceId(this.props.reportConfig, location);

        if (dataSourceId === this.props.reportConfig.rawDataSourceId) {
            return this.props.reportConfig.variables ?? [];
        } else {
            return [];
        }
    }

    getLocation(field: DataSourceField): ReportLogicLocation {

        let location = {} as ReportLogicLocation;

        location.statement = locationType.statement.aggregatedRecordField;
        location.statementNumber = this.props.declarationBatchNumber as number;

        if (field.fieldId) {
            location.fieldId = field.fieldId;
            location.subType = locationType.statement.aggregatedRawField;
        }

        if (field.variableNumber) {
            location.fieldId = field.variableNumber;
            location.subType = locationType.statement.aggregatedVariable;
        }

        return location;
    }

    onReorderConditions(field: DataSourceField, error?: string) {

        if (error && !field.errors.includes(error)) {
            field.errors.push(error);
        }
        let location = this.getLocation(field);
        this.props.onDeclarationChange(location, field);
    }

    isSyncedAggregation(): boolean {
        let aggregation = this.props.reportConfig.aggregations.find(x => x.number === this.props.declarationBatchNumber);

        return aggregation ? typeHelper.isNumber(aggregation.syncedWithId) : false;
    }

    onClearClick(location: ReportLogicLocation, field: DataSourceField, conditionNumber: number, isValue?: boolean): Promise<boolean> {

        let newField = assignmentHelper.clearOperandsInDeclaration(location, field, conditionNumber, isValue) as DataSourceField;

        this.props.onDeclarationChange(location, newField);

        return Promise.resolve(false);
    }

    onAddFieldClick(): void {

        let location = { statementNumber: this.props.declarationBatchNumber as number } as ReportLogicLocation;

        this.props.onAddDeclarationClick((f) => {

            let fieldName = this.state.selectedField || this.defaultField;

            var definitions = this.getDefinitions();
            var definition = definitions.find(x => x.name === fieldName);
            if (definition) {
                Object.assign(f, aggregationHelper.buildFromField(definition.id as number));
                return;
            }

            var variables = this.getVariables();
            var variable = variables.find(x => x.name === fieldName);
            if (variable) {
                Object.assign(f, aggregationHelper.buildFromVariable(variable.number));
                return;
            }
        }, undefined, undefined, location);
    }

    onRemoveFieldClick(field: DataSourceField): void {
        this.props.onRemoveDeclarationClick(this.getLocation(field));
    }

    getAssignmentContent(field: DataSourceField, isReadOnly: boolean): JSX.Element {

        let location = this.getLocation(field);

        return (
            <>
                <AssignmentBuilder
                    reportConfigType={this.props.reportConfig.type}
                    isReadOnly={isReadOnly}
                    location={location}
                    assignment={field.assignment}
                    fieldName=''
                    functions={this.props.reportConfig.userDefinedFunctions}
                    globalFunctions={this.props.collections.globalModuleConfig.userDefinedFunctions}
                    lookups={this.props.reportConfig.lookups}
                    dictionaries={this.props.reportConfig.dictionaries}
                    variables={this.props.reportConfig.variables}
                    dataSources={this.props.collections.dataSources}
                    cases={this.props.reportConfig.cases}
                    aggregations={this.props.reportConfig.aggregations}
                    reportFields={[]}
                    customDataSourceFields={null as any}
                    onAddAssignmentClick={this.props.onAddAssignmentClick}
                    onRemoveAssignmentClick={this.props.onRemoveAssignmentClick}
                    onResetAssignmentClick={this.onResetAssignmentClick}
                    onLogicalOperatorChange={this.props.onLogicalOperatorChange}
                    onParenthesesChange={this.props.onParenthesesChange}
                    onAddConditionClick={this.props.onAddConditionClick}
                    onRemoveConditionClick={(location, keyword, event) => this.props.onRemoveConditionClick(location, event)}
                    onComparisonTypeChange={this.props.onComparisonTypeChange}
                    onOperandClick={this.props.onOperandClick}
                    onCopyClick={this.props.onCopyClick}
                    onPasteClick={this.props.onPasteClick}
                    onValidateFieldClick={this.props.onValidateDeclarationClick}
                    allowPaste={this.props.allowPaste}
                    onClearClick={(location: ReportLogicLocation, conditionNumber: number, isValue?: boolean) => this.onClearClick(location, field, conditionNumber, isValue)}
                    onReorderAssignmentConditions={(error) => { this.onReorderConditions(field, error); }} />

                <ErrorList errors={field.errors} className="mt-2" />
            </>
        );
    }

    getFieldReferences(id: number, aggregationNumber: number): SelectOption[] {
        return reportLogicReferenceHelper.getAggregationFieldReferences(id, this.props.reportConfig, this.props.collections, aggregationNumber);
    }

    getOptions(
        existingFields: DataSourceField[],
        definitions: FieldDefinition[],
        variables: ReportVariable[]): SelectSearchOption[] {

        const createOptionFromField = (definition: FieldDefinition): SelectSearchOption => {
            return {
                name: definition.name,
                value: definition.name
            };
        };

        const createOptionFromVariable = (variable: ReportVariable): SelectSearchOption => {
            return {
                name: variable.name,
                value: variable.name
            };
        };

        var optionsForFields = definitions
            .filter(f => !existingFields.some(field => field.fieldId === f.id))
            .map(createOptionFromField);

        var optionsForVariables = variables
            .filter(f => !existingFields.some(field => field.variableNumber === f.number))
            .map(createOptionFromVariable);

        var combinedOptions = optionsForFields.concat(optionsForVariables);

        return combinedOptions;
    }

    onFieldSelect(selectedValue: SelectedOptionValue | SelectedOptionValue[]): void {

        let changes = {
            selectedField: selectedValue.toString(),
        };

        this.setState(changes);
    }

    render(): JSX.Element {

        let isReadOnly = this.props.isReadOnly || this.isSyncedAggregation();

        let existingFields = typeHelper.isNumber(this.props.declarationBatchNumber) ? this.getFields() : [];

        let definitions = typeHelper.isNumber(this.props.declarationBatchNumber)  ? this.getDefinitions() : [];
        let variables = typeHelper.isNumber(this.props.declarationBatchNumber)  ? this.getVariables() : [];

        let options = this.getOptions(existingFields, definitions, variables);
        if (options.length > 0 && !this.state.selectedField) {
            this.defaultField = options[0].value;
        }

        return (
            <>
                {
                    typeHelper.isNumber(this.props.declarationBatchNumber) &&
                    <>
                        <div className="section-heading">
                            <InfoIcon info={guidance.aggregatedRecord} />
                            <h4>Aggregated Record Fields</h4>
                        </div>

                        {options.length > 0 &&
                            <div className="row no-gutters mb-3">
                                <div className="col-auto">
                                    <SelectSearch
                                        options={this.getOptions(existingFields, definitions, variables)}
                                        value={this.state.selectedField || this.defaultField}
                                        onChange={this.onFieldSelect}
                                        search={true} />
                                </div>
                                <div className="col ml-2">
                                    <button
                                        onClick={this.onAddFieldClick}
                                        className="btn btn-light">+ Field</button>
                                </div>
                            </div>
                        }
                        
                        <ReportLogicList
                            isWaiting={this.props.isWaiting}
                            items={existingFields}
                            columns={getColumns(this.onCopyFieldClick, this.onRemoveFieldClick, isReadOnly, this.getFieldReferences, onReferenceSelected, this.props.declarationBatchNumber || 0, this.props.reportConfig.id as number, definitions, variables)}
                            getContent={x => this.getAssignmentContent(x, isReadOnly)}
                            getKey={x => (x.fieldId || x.variableNumber) as number}
                            createHtmlId={x => reportConfigNavigation.buildAggregatedRecordFieldId((x.fieldId || x.variableNumber) as number)} />
                    </>
                }
            </>
        );
    }
}

export default AggregatedRecordBuilder;
